import React, { useMemo, useState } from 'react';
import { Btn, Msg, Section, Select, panelPost } from '../kit';
import { Labeled, inputCls } from '../email/emailKit';
import {
  FALLBACKS, type HeroSetting, type HomepageData, METRICS, type Metric, type MonthFallback, NEW_MODES, type NewMode,
  type Note, REFRESH_FALLBACKS, type RefreshFallback, type RefreshRow, Radio, S, SaveBar, type TabProps, Toggle, fmt,
  parseCount, useDraft,
} from './shared';

/**
 * Hero numbers — the three counts under the homepage search (Universities &
 * Labs, Tech Offers, Countries). Each shows the live count or a typed number,
 * and its "+X NEW!" line by default shows what the latest DATA REFRESH added
 * (owner, 2026-10-11: "keep it persistent since the last refresh, not start of
 * the month"). A refresh is one load: technologies loaded no more than
 * refresh_gap_hours (default 12) apart. While the latest refresh added none of
 * a metric, the refresh fallback shows the latest refresh that did (or its own
 * 0, which hides the line). The admin can instead count calendar months (this
 * month's additions, India time, with the month fallback until it has any),
 * pick a month, a rolling window, or type the number. Below the form, the
 * latest data refreshes and the last 24 months of additions, with the refresh /
 * month each +NEW line uses marked.
 * POST homepage/save_hero { hero, month_fallback, refresh_fallback,
 * refresh_gap_hours }; the homepage stats endpoint is versioned on these
 * settings, so a save shows on the next load. The server groups refreshes with
 * the SAVED gap, so the counts for a new gap show after saving.
 */

/** homepage_content._int_or_none's ceilings — mirrored so the form can say so first. */
const VALUE_MAX = 10_000_000;
const NEW_MAX = 1_000_000;
const DEFAULT_WINDOW = '30d';
const DEFAULT_MODE: NewMode = 'refresh';
const DEFAULT_FALLBACK: MonthFallback = 'latest';
const DEFAULT_REFRESH_FALLBACK: RefreshFallback = 'latest';
/** homepage_content.DEFAULT_OPTIONS / GAP_HOURS_RANGE, for a payload without them. */
const DEFAULT_GAP_HOURS = 12;
const GAP_RANGE: [number, number] = [1, 168];
/** Months in the additions table before "Show all". */
const MONTHS_FIRST = 12;
/** Refreshes in the refresh table before "Show all". */
const REFRESHES_FIRST = 10;
/** Names listed under a count before "+N more". */
const NAMES_FIRST = 3;

/** Short radio names; the backend's longer wording (new_modes) is the tooltip. */
const MODE_LABELS: Record<NewMode, string> = {
  refresh: 'Since the last data refresh', monthly: 'Monthly', month: 'A month', window: 'Rolling window',
  manual: 'Manual number',
};
/** Short choice names for the fallback picker (the backend's sentence explains the chosen one). */
const FALLBACK_LABELS: Record<MonthFallback, string> = {
  latest: 'The latest month with additions',
  previous: 'Last month, even if it had none',
  none: 'Nothing until this month has some',
};
/** The same for the refresh fallback (the backend's refresh_fallbacks sentence explains the chosen one). */
const REFRESH_FALLBACK_LABELS: Record<RefreshFallback, string> = {
  latest: 'The latest refresh that added some',
  none: 'Its own 0 (the line hides)',
};
/** The additions tables' column order — tech offers first, as the owner reads it. */
const TABLE_METRICS: Metric[] = ['tech_offers', 'universities', 'countries'];

type MetricDraft = {
  value_mode: 'live' | 'manual'; value: string;
  new_mode: NewMode; new_month: string; new_window: string; new_value: string; show_new: boolean;
};
type HeroDraft = {
  hero: Record<Metric, MetricDraft>; month_fallback: MonthFallback;
  refresh_fallback: RefreshFallback; refresh_gap_hours: string;
};
type Settings = HomepageData['settings'];

const str = (v: number | null | undefined): string => (v === null || v === undefined ? '' : String(v));
/** 'auto' was a rolling window before 2026-10-11; anything unknown is the default, refresh. */
const asMode = (v: unknown): NewMode =>
  v === 'auto' ? 'window' : (NEW_MODES as unknown[]).includes(v) ? (v as NewMode) : DEFAULT_MODE;
const asFallback = (v: unknown): MonthFallback =>
  (FALLBACKS as unknown[]).includes(v) ? (v as MonthFallback) : DEFAULT_FALLBACK;
const asRefreshFallback = (v: unknown): RefreshFallback =>
  (REFRESH_FALLBACKS as unknown[]).includes(v) ? (v as RefreshFallback) : DEFAULT_REFRESH_FALLBACK;

const draftFrom = (s: Settings): HeroDraft => {
  const hero = {} as Record<Metric, MetricDraft>;
  METRICS.forEach(m => {
    const h: Partial<HeroSetting> = s?.hero?.[m] || {};
    hero[m] = {
      value_mode: h.value_mode === 'manual' ? 'manual' : 'live',
      value: str(h.value),
      new_mode: asMode(h.new_mode),
      new_month: h.new_month || '',
      new_window: h.new_window || DEFAULT_WINDOW,
      new_value: str(h.new_value),
      show_new: h.show_new !== false,
    };
  });
  const o = s?.hero_options;
  return {
    hero, month_fallback: asFallback(o?.month_fallback), refresh_fallback: asRefreshFallback(o?.refresh_fallback),
    refresh_gap_hours: str(o?.refresh_gap_hours ?? DEFAULT_GAP_HOURS),
  };
};

/** The hours the server accepts between two refreshes (gap_hours_range), or the built-in 1–168. */
const gapRange = (data: HomepageData): [number, number] => {
  const r = data.gap_hours_range;
  return Array.isArray(r) && r.length === 2 && r.every(x => Number.isFinite(Number(x)))
    ? [Number(r[0]), Number(r[1])] : GAP_RANGE;
};

/** The typed hours between two refreshes: a whole number in range, or the error a save shows. */
const parseGap = (raw: string, [lo, hi]: [number, number]): { value: number | null; error: string } => {
  const p = parseCount(raw, hi, 'Hours between two refreshes');
  if (p.error || p.value === null || p.value < lo) {
    return { value: null, error: `Hours between two refreshes must be a whole number from ${lo} to ${hi}` };
  }
  return p;
};

/** '2026-09' → 'September 2026' (the backend's month_label), for a month the table does not list. */
const monthLabel = (k: string): string => {
  const x = /^(\d{4})-(\d{2})$/.exec(k || '');
  if (!x) return k;
  return new Date(Number(x[1]), Number(x[2]) - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' });
};

const IST_TIME: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false };
/** A refresh's load span in India time, '10:42–11:05 IST' ('' when the times do not parse).
 *  start / end are UTC without a zone (created_date is naive UTC). */
const loadTimes = (r: RefreshRow): string => {
  const at = (v: string) => {
    const t = new Date(/(Z|[+-]\d\d:?\d\d)$/i.test(v || '') ? v : `${v}Z`);
    return Number.isNaN(t.getTime()) ? '' : t.toLocaleTimeString('en-GB', IST_TIME);
  };
  const a = at(r.start), b = at(r.end);
  if (!a || !b) return '';
  return a === b ? `${a} IST` : `${a}–${b} IST`;
};

/** The edited options a +NEW pick depends on. `gapEdited`: the typed gap is not the saved one, so the
 *  server's refresh counts (grouped with the saved gap) do not apply yet; `gapNeed` says so. */
type Ctx = { fb: MonthFallback; rfb: RefreshFallback; gapEdited: boolean; gapNeed: string };

/** One metric's +NEW under the edited settings: the count (null = not decided
 *  yet, `need` says what is missing), its label ("11 Oct 2026 refresh",
 *  "October 2026", "Last 30 days", …) and the month or refresh it counts. */
type NewPick = { n: number | null; label: string; month: string | null; refresh: string | null; need: string };

const pickNew = (data: HomepageData, m: Metric, d: MetricDraft, c: Ctx): NewPick => {
  if (d.new_mode === 'manual') {
    return { n: parseCount(d.new_value, NEW_MAX, '').value, label: 'typed number', month: null, refresh: null,
      need: 'type the +NEW number' };
  }
  if (d.new_mode === 'window') {
    return { n: Number(data.deltas?.[d.new_window]?.[m] ?? 0), label: data.windows?.[d.new_window] || d.new_window,
      month: null, refresh: null, need: '' };
  }
  if (d.new_mode === 'refresh') {
    if (c.gapEdited) return { n: null, label: '', month: null, refresh: null, need: c.gapNeed };
    const p = data.refresh_preview?.[c.rfb]?.[m];
    if (!p) return { n: null, label: '', month: null, refresh: null, need: 'Refresh to see the latest data refresh’s count' };
    return { n: Number(p.value || 0), label: p.label || 'no data refresh yet', month: null, refresh: p.refresh || null,
      need: '' };
  }
  if (d.new_mode === 'month') {
    if (!d.new_month) return { n: null, label: '', month: null, refresh: null, need: 'pick a month' };
    const row = (data.months || []).find(r => r.month === d.new_month);
    return { n: row ? Number(row[m] || 0) : null, label: row?.label || monthLabel(d.new_month), month: d.new_month,
      refresh: null, need: `${monthLabel(d.new_month)} — its count shows after saving` };
  }
  const p = data.monthly_preview?.[c.fb]?.[m];
  if (!p) return { n: null, label: '', month: null, refresh: null, need: 'Refresh to see this month’s count' };
  return { n: Number(p.value || 0), label: p.label || monthLabel(p.month), month: p.month || null, refresh: null,
    need: '' };
};

/** What one metric would show: the number, and the +NEW count the homepage
 *  prints only when it is above 0. null = a box that is empty or bad. */
type Shown = { value: number | null; n: number | null; off: boolean; label: string; need: string };

const fromServer = (data: HomepageData, m: Metric): Shown => {
  const s = data.shown?.[m];
  const h = data.settings.hero?.[m];
  const off = s ? s.show_new === false : h?.show_new === false;
  const label = s?.label === 'manual' ? 'typed number' : (s?.label || '');
  return { value: Number(s?.value ?? data.live?.[m] ?? 0), n: off ? 0 : Number(s?.new ?? 0), off, label, need: '' };
};

const fromDraft = (data: HomepageData, m: Metric, d: MetricDraft, c: Ctx): Shown => {
  const value = d.value_mode === 'manual' ? parseCount(d.value, VALUE_MAX, '').value : Number(data.live?.[m] ?? 0);
  const p = pickNew(data, m, d, c);
  return { value, n: d.show_new ? p.n : 0, off: !d.show_new, label: p.label, need: p.need };
};

const ShownLine: React.FC<{ s: Shown }> = ({ s }) => (
  <>
    <span className="font-bold text-grayscale-1300">{s.value === null ? 'type a number' : fmt(s.value)}</span>
    {' · '}
    {s.off ? <span>+NEW line hidden (switched off)</span>
      : s.n === null ? <span className="text-amber-700">{s.need || 'not set yet'}</span>
        : s.n > 0 ? (
          <>
            <span className="font-semibold text-secondary-og-600">+{fmt(s.n)} NEW!</span>
            {s.label && <span> — {s.label}</span>}
          </>
        ) : <span>+NEW line hidden (0){s.label ? ` — ${s.label}` : ''}</span>}
  </>
);

/** A small rounded label inside a table cell. */
const Pill: React.FC<{ tone: 'primary' | 'green' | 'muted'; title?: string; children: React.ReactNode }> =
  ({ tone, title, children }) => (
  <span title={title} className={`inline-block rounded-full border px-1.5 py-px text-[10px] leading-4 whitespace-nowrap
    ${tone === 'primary' ? 'bg-white border-primary-300 text-primary-700'
      : tone === 'green' ? 'bg-green-50 border-green-200 text-green-700'
        : 'bg-grayscale-200 border-grayscale-300 text-grayscale-1100'}`}>
    {children}
  </span>
);

/** The first few names under a count, and the rest on request. */
const Names: React.FC<{ names: string[] }> = ({ names }) => {
  const [open, setOpen] = useState(false);
  if (!names?.length) return null;
  const list = open ? names : names.slice(0, NAMES_FIRST);
  return (
    <div className="text-[11px] text-grayscale-1100 leading-snug mt-0.5">
      {list.join(', ')}
      {names.length > NAMES_FIRST && (
        <button type="button" onClick={() => setOpen(!open)} className="ml-1 text-primary-700 underline">
          {open ? 'show less' : `+${fmt(names.length - NAMES_FIRST)} more`}
        </button>
      )}
    </div>
  );
};

/** Each fallback choice × metric: what the +NEW line gives right now, the chosen choice highlighted. */
function ChoiceTable<F extends string>({ choices, chosen, names, pick, label, uses, offNote }: {
  choices: readonly F[]; chosen: F; names: Record<F, string>;
  pick: (f: F, m: Metric) => { value: number; label: string } | undefined;
  label: (m: Metric) => string;
  /** The metric is on this mode (the others are greyed). */
  uses: (m: Metric) => boolean; offNote: string;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] text-xs">
        <thead className="text-grayscale-1100">
          <tr>
            <th className="text-left font-medium py-1 pr-3">Each choice, right now</th>
            {METRICS.map(m => (
              <th key={m} className="text-left font-medium py-1 pr-3">
                {label(m)}
                {!uses(m) && <span className="font-normal"> · {offNote}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {choices.map(f => (
            <tr key={f} className={`border-t border-grayscale-300 align-top ${f === chosen ? 'bg-primary-50' : ''}`}>
              <td className="py-1.5 pr-3 pl-1 text-grayscale-1200">
                <span className={f === chosen ? 'font-semibold text-grayscale-1300' : ''}>{names[f]}</span>
                {f === chosen && <> <Pill tone="primary">chosen</Pill></>}
              </td>
              {METRICS.map(m => {
                const p = pick(f, m);
                const live = uses(m);
                return (
                  <td key={m} className={`py-1.5 pr-3 ${live ? 'text-grayscale-1200' : 'text-grayscale-900'}`}>
                    {!p ? '—' : (
                      <>
                        <span className={p.value > 0 && live ? 'font-semibold text-secondary-og-600' : ''}>
                          {p.value > 0 ? `+${fmt(p.value)} NEW!` : 'line hidden (0)'}
                        </span>
                        <div className="text-[11px] text-grayscale-1100">{p.label}</div>
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The counts and names a month row and a refresh row share. */
type Additions = Record<Metric, number> & { university_names: string[]; country_names: string[] };
/** The row (a month, or a refresh id) each metric's +NEW line uses under the edited settings, and how to mark it. */
type InUse = { key: string; shown: boolean; edited: boolean };

/** Additions per month or per refresh, newest first, with the row each +NEW line uses marked. Scrolls in
 *  its own box on a phone. */
function AdditionsTable<R extends Additions>({
  title, note, head, rows, rowKey, current, firstCell, first, unit, inUse, label,
}: {
  title: string; note: React.ReactNode; head: string; rows: R[]; rowKey: (r: R) => string;
  /** The row to highlight: this month, the latest refresh. */
  current: (r: R, i: number) => boolean;
  firstCell: (r: R, i: number) => React.ReactNode;
  /** Rows before "Show all". */
  first: number;
  /** ['month', 'months'] — for the buttons and pill tooltips. */
  unit: [string, string];
  inUse: Partial<Record<Metric, InUse>>; label: (m: Metric) => string;
}) {
  const [all, setAll] = useState(false);
  // Never hide the row a +NEW line uses behind "Show all".
  const deepest = Math.max(-1, ...Object.values(inUse).map(u => rows.findIndex(r => rowKey(r) === u?.key)));
  const firstN = Math.max(first, deepest + 1);
  const list = all ? rows : rows.slice(0, firstN);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-x-3 gap-y-1 flex-wrap">
        <div className="text-sm font-bold text-grayscale-1300 font-grotesk">{title}</div>
        <div className="text-xs text-grayscale-1100">{note}</div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-grayscale-300">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-grayscale-200 text-xs text-grayscale-1100">
            <tr>
              <th className="text-left font-medium px-3 py-2 w-32 sm:w-40">{head}</th>
              {TABLE_METRICS.map(m => (
                <th key={m} className={`text-left font-medium px-3 py-2 ${m === 'tech_offers' ? 'w-44' : ''}`}>
                  {label(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={rowKey(r)}
                className={`border-t border-grayscale-300 align-top ${current(r, i) ? 'bg-primary-50' : ''}`}>
                <td className="px-3 py-2 whitespace-nowrap">{firstCell(r, i)}</td>
                {TABLE_METRICS.map(m => {
                  const n = Number(r[m] || 0);
                  const u = inUse[m];
                  const names = m === 'universities' ? r.university_names : m === 'countries' ? r.country_names : [];
                  return (
                    <td key={m} className="px-3 py-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={n > 0 ? 'font-semibold text-grayscale-1300' : 'text-grayscale-900'}>
                          {n > 0 ? `+${fmt(n)}` : '0'}
                        </span>
                        {u?.key === rowKey(r) && (u.shown
                          ? <Pill tone="green" title={`${label(m)}: the homepage's +NEW line uses this ${unit[0]}`
                              + (u.edited ? ' once you save' : '')}>
                              {u.edited ? 'on homepage after save' : 'on homepage'}
                            </Pill>
                          : <Pill tone="muted" title={`${label(m)}: this ${unit[0]} is picked, but the +NEW line is hidden`
                              + ' (switched off, or the count is 0)'}>
                              picked · line hidden
                            </Pill>)}
                      </div>
                      <Names names={names || []} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > list.length && (
        <div><Btn small onClick={() => setAll(true)}>Show all {rows.length} {unit[1]}</Btn></div>
      )}
      {all && rows.length > firstN && (
        <div><Btn small onClick={() => setAll(false)}>Show fewer {unit[1]}</Btn></div>
      )}
    </div>
  );
}

const HeroNumbers: React.FC<TabProps> = ({ data, onSaved, onDirty }) => {
  const { draft, edit, dirty, discard, settle } = useDraft(data.settings, draftFrom, onDirty);
  const [busy, setBusy] = useState<'' | 'save' | 'reset'>('');
  const [msg, setMsg] = useState<Note>(null);
  const label = (m: Metric) => data.metric_labels?.[m] || m;
  const windows = Object.keys(data.windows || {});
  const months = data.months || [];
  const refreshes = data.refreshes || [];
  const latest = refreshes[0];
  const curLabel = data.current_month_label || monthLabel(data.current_month || '') || 'this month';
  const fb = draft.month_fallback;
  const rfb = draft.refresh_fallback;
  const range = gapRange(data);
  const gap = parseGap(draft.refresh_gap_hours, range);
  const set = (m: Metric, patch: Partial<MetricDraft>) =>
    edit(d => ({ ...d, hero: { ...d.hero, [m]: { ...d.hero[m], ...patch } } }));
  // Which metrics would show something else after saving — only those get an "After saving" line.
  const saved = useMemo(() => draftFrom(data.settings), [data.settings]);
  const savedGap = Number(saved.refresh_gap_hours) || DEFAULT_GAP_HOURS;
  // The server groups refreshes with the saved gap, so a new gap's counts are only known after saving.
  const gapEdited = gap.value !== savedGap;
  const gapNeed = gap.error ? `type the hours between two refreshes (${range[0]}–${range[1]})`
    : `refreshes regrouped at ${gap.value} hours — the count shows after saving`;
  const ctx: Ctx = { fb, rfb, gapEdited, gapNeed };
  // A new fallback only changes a metric on Monthly / refresh whose pick differs under it.
  const pickKey = (f: MonthFallback, m: Metric) => JSON.stringify(data.monthly_preview?.[f]?.[m] ?? null);
  const refreshKey = (f: RefreshFallback, m: Metric) => JSON.stringify(data.refresh_preview?.[f]?.[m] ?? null);
  const changed = (m: Metric) => dirty && (JSON.stringify(draft.hero[m]) !== JSON.stringify(saved.hero[m])
    || (draft.hero[m].new_mode === 'monthly' && pickKey(fb, m) !== pickKey(saved.month_fallback, m))
    || (draft.hero[m].new_mode === 'refresh'
      && (gapEdited || refreshKey(rfb, m) !== refreshKey(saved.refresh_fallback, m))));
  const onMonthly = METRICS.filter(m => draft.hero[m].new_mode === 'monthly');
  const onRefresh = METRICS.filter(m => draft.hero[m].new_mode === 'refresh');
  const thisMonth = months.find(r => r.current);
  // Every metric on Monthly already has additions this month → the three choices agree for now.
  const fallbackIdle = onMonthly.length > 0 && !!thisMonth && onMonthly.every(m => Number(thisMonth[m] || 0) > 0);
  // The latest refresh added some of every metric on refresh → both choices agree for now.
  const refreshIdle = onRefresh.length > 0 && !!latest && !gapEdited
    && onRefresh.every(m => Number(latest[m] || 0) > 0);

  // The month / refresh each metric's +NEW line uses under the edited settings.
  const monthUse: Partial<Record<Metric, InUse>> = {};
  const refreshUse: Partial<Record<Metric, InUse>> = {};
  METRICS.forEach(m => {
    const d = draft.hero[m];
    if (d.new_mode !== 'monthly' && d.new_mode !== 'month' && d.new_mode !== 'refresh') return;
    const p = pickNew(data, m, d, ctx);
    const use = { shown: d.show_new && (p.n ?? 0) > 0, edited: changed(m) };
    if (p.refresh) refreshUse[m] = { key: p.refresh, ...use };
    else if (p.month) monthUse[m] = { key: p.month, ...use };
  });

  const save = async () => {
    if (gap.error) { setMsg({ kind: 'error', text: gap.error }); return; }
    const hero: Record<string, any> = {};
    for (const m of METRICS) {
      const d = draft.hero[m];
      const v = parseCount(d.value, VALUE_MAX, `${label(m)} number`);
      const n = parseCount(d.new_value, NEW_MAX, `${label(m)} +NEW number`);
      // A box only has to be right when it is in use; an unused one is kept if it parses.
      const err = (d.value_mode === 'manual' ? v.error : '') || (d.new_mode === 'manual' ? n.error : '')
        || (d.value_mode === 'manual' && v.value === null ? `${label(m)}: type the number to show, or switch it back to Live` : '')
        || (d.new_mode === 'manual' && n.value === null ? `${label(m)}: type the +NEW number, or pick another +NEW count` : '')
        || (d.new_mode === 'month' && !d.new_month ? `${label(m)}: pick the month whose additions to show` : '');
      if (err) { setMsg({ kind: 'error', text: err }); return; }
      hero[m] = { value_mode: d.value_mode, value: v.error ? null : v.value, new_mode: d.new_mode,
        new_month: d.new_month || null, new_window: d.new_window, new_value: n.error ? null : n.value,
        show_new: d.show_new };
    }
    setBusy('save'); setMsg(null);
    const { data: next, error } = await panelPost(S, 'save_hero', {
      hero, month_fallback: draft.month_fallback, refresh_fallback: draft.refresh_fallback,
      refresh_gap_hours: gap.value,
    });
    setBusy('');
    if (error || !next) { setMsg({ kind: 'error', text: error || 'Save failed' }); return; }
    onSaved(next);
    settle();
    setMsg({ kind: 'ok', text: 'Saved — the homepage shows the new numbers on its next load.' });
  };

  const reset = async () => {
    setBusy('reset'); setMsg(null);
    const { data: next, error } = await panelPost(S, 'reset', { section: 'hero' });
    setBusy('');
    if (error || !next) { setMsg({ kind: 'error', text: error || 'Reset failed' }); return; }
    onSaved(next);
    settle();
    setMsg({ kind: 'ok', text: 'Back to the defaults: live counts; +NEW = since the last data refresh (loads no more '
      + `than ${DEFAULT_GAP_HOURS} hours apart are one refresh), or the latest refresh that added some when the last `
      + 'one added none; Monthly falls back to the latest month with additions; line shown.' });
  };

  const numCls = 'h-9 w-32 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm text-grayscale-1300';

  return (
    <Section title="Hero numbers"
      right={(
        <span className="text-xs text-grayscale-1100">
          {latest ? `Latest refresh: ${latest.label} · ` : ''}This month: {curLabel} (India time)
        </span>
      )}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        The three numbers under the homepage search. Each shows the live count or a number you type, with a
        "+X NEW!" line under it — by default what the latest data refresh added, which stays until the next
        refresh (it does not reset on the 1st of the month). You can instead count calendar months, pick a month, a
        rolling window, or type the number. The homepage prints the line only when that count is above 0.
      </p>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      {/* The shared options for every metric on "Since the last data refresh". */}
      <div className={`rounded-lg border border-grayscale-300 p-3 flex flex-col gap-2
        ${onRefresh.length ? '' : 'opacity-60'}`}>
        <div className="text-sm font-bold text-grayscale-1300 font-grotesk">Since the last data refresh</div>
        <p className="text-xs text-grayscale-1100">
          A data refresh is one load of new technologies: loads no more than the hours below apart count as one
          refresh. The +NEW line shows what the latest refresh added, until the next refresh.
          {onRefresh.length === 0 && ' No metric uses it right now, so these have no effect.'}
        </p>
        <div className="flex gap-3 flex-wrap items-end">
          <Labeled label="When the last refresh added none of a metric" span="w-full sm:w-auto sm:max-w-md">
            <select value={rfb} onChange={e => edit(d => ({ ...d, refresh_fallback: asRefreshFallback(e.target.value) }))}
              aria-label="When the last refresh added none of a metric" className={inputCls}>
              {REFRESH_FALLBACKS.map(f => <option key={f} value={f}>{REFRESH_FALLBACK_LABELS[f]}</option>)}
            </select>
          </Labeled>
          <Labeled label="Hours between two refreshes" hint={`${range[0]}–${range[1]}`} span="w-full sm:w-56">
            <input type="number" min={range[0]} max={range[1]} step={1} value={draft.refresh_gap_hours}
              aria-label="Hours between two refreshes"
              onChange={e => edit(d => ({ ...d, refresh_gap_hours: e.target.value }))} className={inputCls} />
          </Labeled>
        </div>
        <p className="text-xs text-grayscale-1100">
          {data.refresh_fallbacks?.[rfb] ? `${data.refresh_fallbacks[rfb]}. ` : ''}
          It only matters for a metric the latest refresh added none of (say a load from universities already
          listed adds no university).
          {refreshIdle && ` The latest refresh (${latest?.label}) added some of every metric that uses it, so both `
            + 'choices show the same right now.'}
        </p>
        <p className="text-xs text-grayscale-1100">
          Loads closer together than {gap.value ?? savedGap} hours count as one refresh, so a university loaded over
          an evening and the next morning counts once; a longer pause starts a new refresh.
          {gap.error
            ? <span className="text-amber-700"> {gap.error}.</span>
            : gapEdited && ` The counts here and in the Data refreshes table use the saved ${savedGap} hours; they `
              + 'update after saving.'}
        </p>
        <div className={gapEdited ? 'opacity-60' : ''}>
          <ChoiceTable choices={REFRESH_FALLBACKS} chosen={rfb} names={REFRESH_FALLBACK_LABELS}
            pick={(f, m) => data.refresh_preview?.[f]?.[m]} label={label}
            uses={m => draft.hero[m].new_mode === 'refresh'} offNote="not on refresh" />
        </div>
      </div>

      {/* The shared fallback for every metric on "Monthly". */}
      <div className={`rounded-lg border border-grayscale-300 p-3 flex flex-col gap-2
        ${onMonthly.length ? '' : 'opacity-60'}`}>
        <Labeled label={`Until ${curLabel} has additions, Monthly shows`} span="w-full sm:w-auto sm:max-w-md">
          <select value={fb} onChange={e => edit(d => ({ ...d, month_fallback: asFallback(e.target.value) }))}
            aria-label={`Until ${curLabel} has additions, Monthly shows`} className={inputCls}>
            {FALLBACKS.map(f => <option key={f} value={f}>{FALLBACK_LABELS[f]}</option>)}
          </select>
        </Labeled>
        <p className="text-xs text-grayscale-1100">
          {data.fallbacks?.[fb] ? `${data.fallbacks[fb]}. ` : ''}
          It only matters for a metric on Monthly, while {curLabel} has no additions for it yet (say on the 1st,
          before the month’s first load).
          {onMonthly.length === 0 && ' No metric is on Monthly right now, so it has no effect.'}
          {fallbackIdle && ` ${curLabel} already has additions for every metric on Monthly, so all three `
            + 'choices show the same right now.'}
        </p>
        <ChoiceTable choices={FALLBACKS} chosen={fb} names={FALLBACK_LABELS}
          pick={(f, m) => data.monthly_preview?.[f]?.[m]} label={label}
          uses={m => draft.hero[m].new_mode === 'monthly'} offNote="not on Monthly" />
      </div>

      {METRICS.map(m => {
        const d = draft.hero[m];
        const live = Number(data.live?.[m] ?? 0);
        const monthly = data.monthly_preview?.[fb]?.[m];
        const fresh = data.refresh_preview?.[rfb]?.[m];
        const windowLabels: Record<string, string> = {};
        windows.forEach(w => {
          windowLabels[w] = `${data.windows[w]} (+${fmt(data.deltas?.[w]?.[m] ?? 0)})`;
        });
        // Months to pick from: the table's, plus a saved month older than it.
        const monthKeys = months.map(r => r.month);
        if (d.new_month && !monthKeys.includes(d.new_month)) monthKeys.push(d.new_month);
        const monthLabels: Record<string, string> = {};
        monthKeys.forEach(k => {
          const r = months.find(x => x.month === k);
          const n = Number(r?.[m] || 0);
          monthLabels[k] = !r ? `${monthLabel(k)} (older)`
            : `${r.label} (${n > 0 ? `+${fmt(n)}` : '0'})${r.current ? ' · this month' : ''}`;
        });
        const startMonth = [monthly?.month, data.current_month].find(k => k && monthKeys.includes(k)) || monthKeys[0] || '';
        const pick = pickNew(data, m, d, ctx);
        return (
          <div key={m} className="rounded-lg border border-grayscale-300 p-3 flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <div className="text-sm font-bold text-grayscale-1300 font-grotesk">{label(m)}</div>
              <div className="text-xs text-grayscale-1100">Live count {fmt(live)}</div>
            </div>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-grayscale-1100">Number shown</span>
                <Radio name={`${m}-value`} checked={d.value_mode === 'live'}
                  onChange={() => set(m, { value_mode: 'live' })}>
                  Live ({fmt(live)})
                </Radio>
                <div className="flex items-center gap-2 flex-wrap">
                  <Radio name={`${m}-value`} checked={d.value_mode === 'manual'}
                    onChange={() => set(m, { value_mode: 'manual', value: d.value || String(live) })}>
                    Manual
                  </Radio>
                  {d.value_mode === 'manual' && (
                    <input type="number" min={0} max={VALUE_MAX} step={1} value={d.value}
                      aria-label={`${label(m)}: number to show`}
                      onChange={e => set(m, { value: e.target.value })} className={numCls} />
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-grayscale-1100">+NEW line</span>
                <div className="flex items-center gap-2">
                  <Toggle on={d.show_new} onChange={v => set(m, { show_new: v })}
                    label={`${label(m)}: show the +NEW line`} />
                  <span className="text-sm text-grayscale-1200">
                    {d.show_new ? 'Shown under the number' : 'Hidden'}
                  </span>
                </div>
              </div>

              <div className={`flex flex-col gap-1.5 md:col-span-2 ${d.show_new ? '' : 'opacity-50'}`}>
                <span className="text-xs text-grayscale-1100">+NEW counts</span>
                <div className="flex items-center gap-x-2 gap-y-1 flex-wrap" title={data.new_modes?.refresh}>
                  <Radio name={`${m}-new`} checked={d.new_mode === 'refresh'}
                    onChange={() => set(m, { new_mode: 'refresh' })}>
                    {MODE_LABELS.refresh}
                  </Radio>
                  {fresh && (
                    <span className="text-xs text-grayscale-1100">
                      {fresh.value > 0 ? `+${fmt(fresh.value)}` : '0'} · {fresh.label || 'no data refresh yet'}
                      {gapEdited && ` (with the saved ${savedGap} hours; updates after saving)`}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-x-2 gap-y-1 flex-wrap" title={data.new_modes?.monthly}>
                  <Radio name={`${m}-new`} checked={d.new_mode === 'monthly'}
                    onChange={() => set(m, { new_mode: 'monthly' })}>
                    {MODE_LABELS.monthly}
                  </Radio>
                  {monthly && (
                    <span className="text-xs text-grayscale-1100">
                      {monthly.value > 0 ? `+${fmt(monthly.value)}` : '0'} · {monthly.label}
                    </span>
                  )}
                </div>
                {monthKeys.length > 0 && (
                  <div className="flex items-center gap-2 flex-wrap" title={data.new_modes?.month}>
                    <Radio name={`${m}-new`} checked={d.new_mode === 'month'}
                      onChange={() => set(m, { new_mode: 'month', new_month: d.new_month || startMonth })}>
                      {MODE_LABELS.month}
                    </Radio>
                    <Select value={d.new_month && monthKeys.includes(d.new_month) ? d.new_month : startMonth}
                      options={monthKeys} labels={monthLabels}
                      onChange={k => set(m, { new_month: k, new_mode: 'month' })} />
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap" title={data.new_modes?.window}>
                  <Radio name={`${m}-new`} checked={d.new_mode === 'window'}
                    onChange={() => set(m, { new_mode: 'window' })}>
                    {MODE_LABELS.window}
                  </Radio>
                  <Select value={windows.includes(d.new_window) ? d.new_window : DEFAULT_WINDOW}
                    options={windows} labels={windowLabels}
                    onChange={w => set(m, { new_window: w, new_mode: 'window' })} />
                </div>
                <div className="flex items-center gap-2 flex-wrap" title={data.new_modes?.manual}>
                  <Radio name={`${m}-new`} checked={d.new_mode === 'manual'}
                    onChange={() => set(m, { new_mode: 'manual', new_value: d.new_value || String(pick.n ?? 0) })}>
                    {MODE_LABELS.manual}
                  </Radio>
                  {d.new_mode === 'manual' && (
                    <input type="number" min={0} max={NEW_MAX} step={1} value={d.new_value}
                      aria-label={`${label(m)}: +NEW number`}
                      onChange={e => set(m, { new_value: e.target.value })} className={numCls} />
                  )}
                </div>
              </div>
            </div>
            <div className="text-xs text-grayscale-1200 bg-grayscale-200 rounded-md px-3 py-2 flex flex-col gap-0.5">
              <span>Homepage shows now: <ShownLine s={fromServer(data, m)} /></span>
              {changed(m) && <span>After saving: <ShownLine s={fromDraft(data, m, d, ctx)} /></span>}
            </div>
          </div>
        );
      })}

      <SaveBar dirty={dirty} busy={busy} onSave={save} onDiscard={() => { discard(); setMsg(null); }}
        onReset={reset} />

      {refreshes.length > 0 && (
        <AdditionsTable title="Data refreshes" head="Refresh" rows={refreshes} rowKey={r => r.id}
          current={(_, i) => i === 0} first={REFRESHES_FIRST} unit={['refresh', 'refreshes']}
          inUse={refreshUse} label={label}
          note={(
            <>
              One row per load, newest first, India time. A university or country counts in the refresh that loaded
              its first technology.
              {(data.refreshes_total ?? 0) > refreshes.length
                && ` The latest ${fmt(refreshes.length)} of ${fmt(data.refreshes_total)}.`}
              {gapEdited && !gap.error && ` Grouped with the saved ${savedGap} hours; saving regroups them at `
                + `${gap.value}.`}
            </>
          )}
          firstCell={(r, i) => (
            <>
              <div className={i === 0 ? 'font-semibold text-grayscale-1300' : 'text-grayscale-1200'}>{r.label}</div>
              {loadTimes(r) && <div className="text-[11px] text-grayscale-1100">{loadTimes(r)}</div>}
              {i === 0 && <Pill tone="primary">latest</Pill>}
            </>
          )} />
      )}

      {months.length > 0 && (
        <AdditionsTable title="Monthly additions" head="Month" rows={months} rowKey={r => r.month}
          current={r => r.current} first={MONTHS_FIRST} unit={['month', 'months']} inUse={monthUse} label={label}
          note="By load month, India time. A university or country counts in the month its first technology was loaded."
          firstCell={r => (
            <>
              <div className={r.current ? 'font-semibold text-grayscale-1300' : 'text-grayscale-1200'}>{r.label}</div>
              {r.current && <Pill tone="primary">this month</Pill>}
            </>
          )} />
      )}
    </Section>
  );
};

export default HeroNumbers;
